class RequestQueue {
  constructor() {
    this.highPriorityQueue = [];
    this.lowPriorityQueue = [];
    this.networkStatus = "good"; // 'good', 'fair', 'poor'
    this.networkSpeed = 0; // in Mbps
    this.maxRetries = 3;
    this.retryDelays = [1000, 3000, 10000]; // Retry delays in ms
    this.concurrentRequests = 0;
    this.maxConcurrentRequests = 3; // Limit concurrent requests
  }

  onCheatingDetected(callback) {
    this.cheatingCallback = callback;
  }

  setNetworkStatus(status, speed = 0) {
    this.networkStatus = status;
    this.networkSpeed = speed;
  }

  // Add a request to the appropriate queue
  addRequest(request, options = { priority: 'normal', retryCount: 0 }) {
    const requestWithOptions = { ...request, options };
    
    if (options.priority === 'high') {
      this.highPriorityQueue.push(requestWithOptions);
    } else {
      this.lowPriorityQueue.push(requestWithOptions);
    }
    
    this.processQueue();
  }

  // Process requests in the queue
  async processQueue() {
    if (this.concurrentRequests >= this.maxConcurrentRequests) return;
    
    
    // Process all high priority requests first
    while (this.highPriorityQueue.length > 0 && this.concurrentRequests < this.maxConcurrentRequests) {
      this.concurrentRequests++;
      const request = this.highPriorityQueue.shift();
      this.executeRequest(request).finally(() => {
        this.concurrentRequests--;
        this.processQueue();
      });
    }
    
    // Process low priority requests based on network conditions
    while (this.lowPriorityQueue.length > 0 && this.canProcessLowPriority() && this.concurrentRequests < this.maxConcurrentRequests) {
      const request = this.lowPriorityQueue.shift();
      this.concurrentRequests++;
      this.executeRequest(request).finally(() => {
        this.concurrentRequests--;
        this.processQueue();
      });
    }
  }

  pauseScreenUploads() {
    this.pauseLowPriority = true;
  }
  
  resumeScreenUploads() {
    this.pauseLowPriority = false;
    this.processQueue();
  }
  
  // Determine if we can process low priority requests
  canProcessLowPriority() {
    // Don't process low priority if network is poor
    if (this.networkStatus === 'poor' || this.pauseLowPriority === true) return false;
    
    // Process fewer low priority requests if network is fair
    if (this.networkStatus === 'fair') {
      return Math.random() > 0.5; // 50% chance to process
    }
    
    // Process all low priority requests if network is good
    return true;
  }

  // Execute a request with retry logic
  async executeRequest(request) {
    const { fn, options } = request;
    let { retryCount } = options;
    
    try {
      await fn();
    } catch (error) {
      console.error('Request failed:', error);
      
      if (retryCount < this.maxRetries) {
        const delay = this.retryDelays[retryCount] || 10000;
        console.log(`Retrying request in ${delay}ms (attempt ${retryCount + 1})`);
        
        // Re-add to queue with delay instead of blocking
        setTimeout(() => {
          this.addRequest(
            { fn },
            { ...options, retryCount: retryCount + 1 }
          );
        }, delay);
      } else {
        console.error('Request failed after max retries');
      }
    }
  }

  // Add screen chunk to low priority queue
  queueScreenChunk(requestFn) {
    this.addRequest(
      { fn: requestFn },
      { priority: 'low', retryCount: 0 }
    );
  }

  // Add high priority request (transcriptions, API calls, etc.)
  queueHighPriorityRequest(requestFn) {
    this.addRequest(
      { fn: requestFn },
      { priority: 'high', retryCount: 0 }
    );
  }

  /**
   * Wait until ALL currently queued low-priority (screen chunk) requests have
   * been dispatched (not necessarily completed, but handed off for execution).
   * Use this before queueing merge_screen_recording to guarantee ordering:
   *   await requestQueue.drainLowPriorityQueue();
   *   requestQueue.queueHighPriorityRequest(mergeRecording);
   *
   * Resolves immediately if the low-priority queue is already empty.
   */
  drainLowPriorityQueue() {
    if (this.lowPriorityQueue.length === 0 && this.concurrentRequests === 0) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const check = () => {
        if (this.lowPriorityQueue.length === 0 && this.concurrentRequests === 0) {
          resolve();
        } else {
          setTimeout(check, 200);
        }
      };
      check();
    });
  }

  /**
   * Wait until BOTH the high- and low-priority queues are empty and no
   * requests are in flight. Use this on the "we're about to close the
   * window" path (forceZeroMarksSubmission) so the merge fetch actually
   * completes before window.close() kills it.
   *
   * Resolves immediately if everything is already idle.
   */
  drainAll() {
    const isIdle = () =>
      this.lowPriorityQueue.length === 0 &&
      (this.highPriorityQueue ? this.highPriorityQueue.length === 0 : true) &&
      this.concurrentRequests === 0;

    if (isIdle()) return Promise.resolve();
    return new Promise((resolve) => {
      const check = () => {
        if (isIdle()) resolve();
        else setTimeout(check, 200);
      };
      check();
    });
  }
}

const requestQueue = new RequestQueue();
export { RequestQueue }; // named export for testing
export default requestQueue;